import React from "react";
import { Play, Pause, RotateCcw, FastForward } from "lucide-react";
import { useClock } from "@/lib/clock";
import { toast } from "sonner";

const PRESETS = [
  { label: "Antes de empezar (hoy)", iso: null }, // null = real time
  { label: "16:50 — Antes del bus", iso: "2026-07-18T16:50:00+02:00" },
  { label: "17:00 — Sale el bus desde Hotel Domo", iso: "2026-07-18T17:00:30+02:00" },
  { label: "17:30 — Llegada · 3 sorpresas se desbloquean", iso: "2026-07-18T17:31:00+02:00" },
  { label: "18:00 — ¡Sí, quiero!", iso: "2026-07-18T18:01:00+02:00" },
  { label: "19:00 — Cóctel (sorpresas a las 19:30)", iso: "2026-07-18T19:05:00+02:00" },
  { label: "19:30 — 4 sorpresas se desbloquean", iso: "2026-07-18T19:31:00+02:00" },
  { label: "21:00 — Convite", iso: "2026-07-18T21:05:00+02:00" },
  { label: "22:30 — 2 sorpresas se desbloquean", iso: "2026-07-18T22:31:00+02:00" },
  { label: "23:00 — Fiesta", iso: "2026-07-18T23:05:00+02:00" },
  { label: "23:30 — 3 sorpresas se desbloquean", iso: "2026-07-18T23:31:00+02:00" },
  { label: "02:30 — Bus de vuelta", iso: "2026-07-19T02:31:00+02:00" },
  { label: "Después de la boda", iso: "2026-07-19T05:00:00+02:00" },
];

function fmt(iso) {
  if (!iso) return "(hora real)";
  const d = new Date(iso);
  return d.toLocaleString("es-ES", {
    weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    timeZone: "Europe/Madrid",
  });
}

export default function DemoPanel() {
  const { demoOn, demoIso, setDemoIso, clearDemo, now } = useClock();

  const apply = (iso) => {
    if (iso === null) clearDemo();
    else setDemoIso(iso);
    toast.success(iso ? `Modo demo activo → ${fmt(iso)}` : "Modo demo desactivado (hora real)");
  };

  return (
    <div data-testid="demo-panel" className="space-y-5">
      <div className="s-card s-card-pad">
        <div className="flex items-center gap-2 mb-2 text-[color:var(--ink-wine)]">
          <FastForward size={18} />
          <span className="font-script-alt text-2xl">Demo del Estado en Vivo</span>
        </div>
        <p className="text-sm text-[color:var(--ink-soft)] mb-4">
          Para que veáis cómo se verá el "ESTADO EN VIVO" durante la boda. Elige un momento y luego abre la página
          <strong> Programa </strong>o<strong> Galería · Sorpresas </strong>en otra pestaña.
          Las sorpresas también se desbloquearán automáticamente según la hora demo, con su confeti y notificación. La cuenta atrás también reflejará el salto.
        </p>

        <div className="rounded-xl bg-[color:var(--bg-blush)] px-4 py-3 mb-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-[color:var(--ink-wine)]">Hora actual usada</p>
            <p className="font-serif text-xl text-[color:var(--ink-deep)]" data-testid="demo-current-time">
              {now.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Madrid" })}
            </p>
            <p className="text-xs text-[color:var(--ink-soft)] mt-0.5">
              {demoOn ? "🎬 Modo demo" : "⏱️ Hora real"}
            </p>
          </div>
          {demoOn ? (
            <button onClick={() => apply(null)} className="btn-outline" data-testid="demo-stop">
              <RotateCcw size={14} /> Volver a hora real
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 text-[color:var(--ink-soft)] text-xs">
              <Pause size={12} /> No hay demo activo
            </span>
          )}
        </div>

        <p className="label">Salta a un momento clave</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.iso || "real"}
              onClick={() => apply(p.iso)}
              className={`text-left rounded-xl border px-3 py-2.5 text-sm transition-all ${
                (p.iso === null && !demoOn) || (p.iso && demoIso === p.iso)
                  ? "bg-[color:var(--ink-wine)] text-white border-transparent"
                  : "border-[color:var(--line)] text-[color:var(--ink-deep)] hover:border-[color:var(--ink-wine)]"
              }`}
              data-testid={`demo-preset-${p.iso || "real"}`}
            >
              <span className="block font-semibold">{p.label}</span>
              <span className={`text-xs ${(p.iso === null && !demoOn) || (p.iso && demoIso === p.iso) ? "text-white/80" : "text-[color:var(--ink-soft)]"}`}>
                {fmt(p.iso)}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="s-card s-card-pad text-sm text-[color:var(--ink-soft)]">
        <p className="font-semibold text-[color:var(--ink-wine)] mb-1">💡 Consejo</p>
        <p>Mientras estés en modo demo, las páginas <em>Programa</em>, <em>Galería</em> e <em>Inicio</em> mostrarán como si fuera ese momento de la boda. Cuando termines de previsualizar, pulsa <strong>Volver a hora real</strong>.</p>
      </div>
    </div>
  );
}
